import { useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AirportShuttleOutlinedIcon from '@mui/icons-material/AirportShuttleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import PageHeader from '@/components/common/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { Permission } from '@/types/auth';
import type { ProcessConfigurationDto } from '@/types/configuration';
import { radius } from '@/theme/tokens';
import CompanyMaster from './company/CompanyMaster';
import WarehouseMaster from './warehouse/WarehouseMaster';
import MaterialMaster from './material/MaterialMaster';
import CategoryMaster from './category/CategoryMaster';
import UomMaster from './uom/UomMaster';
import BomMaster from './bom/BomMaster';
import SupplierMaster from './supplier/SupplierMaster';
import CustomerMaster from './customer/CustomerMaster';
import CarrierMaster from './carrier/CarrierMaster';
import ClientMaster from './client/ClientMaster';
import ClientContractMaster from './clientContract/ClientContractMaster';
import QcCheckpointMaster from './qcCheckpoint/QcCheckpointMaster';

interface MasterTabDef {
  key: string;
  labelKey: string;
  permission: Permission;
  // Same single-or-array AND-semantics gate as AppShell.tsx's NavLeaf.configKey — a tab hides
  // fully (not just flags) when any of its keys is off, matching the left nav's treatment for
  // every other config-gated screen.
  configKey?: keyof ProcessConfigurationDto | (keyof ProcessConfigurationDto)[];
  // Inverse of configKey — hides the tab whenever ANY of these flags is true. Used for masters
  // that stop being relevant once 3PL is enabled (BOM — a pure 3PL warehouse doesn't manufacture).
  hiddenWhenConfigKey?: keyof ProcessConfigurationDto | (keyof ProcessConfigurationDto)[];
  // Escape hatch for a tab whose visibility is an OR of several independent reasons — the flat
  // configKey (AND) / hiddenWhenConfigKey (OR-hide) pair can't express that. Only BOM needs it so
  // far (two unrelated modules consume BOMs on different terms). Mutually exclusive with the two
  // keys above; when set, it alone decides.
  configPredicate?: (config: ProcessConfigurationDto) => boolean;
  Icon: ComponentType<SvgIconProps>;
  Component: ComponentType;
}

// Every master screen lives here as one tab on one page instead of its own sidebar entry/route.
// To add a new one: build it as a normal self-contained page (own queries, own dialogs, same as
// the ones below), then add one entry here — nothing else (routing, nav, permission wiring)
// needs to change as long as its View/Manage permission codes already exist. See
// docs/MODULE_SPEC.md's Masters section.
const MASTER_TABS: MasterTabDef[] = [
  { key: 'company', labelKey: 'tabs.company', permission: PERMISSIONS.COMPANY_VIEW, Icon: ApartmentOutlinedIcon, Component: CompanyMaster },
  { key: 'warehouses', labelKey: 'tabs.warehouses', permission: PERMISSIONS.WAREHOUSE_VIEW, Icon: WarehouseOutlinedIcon, Component: WarehouseMaster },
  { key: 'materials', labelKey: 'tabs.materials', permission: PERMISSIONS.MATERIAL_VIEW, Icon: Inventory2OutlinedIcon, Component: MaterialMaster },
  { key: 'categories', labelKey: 'tabs.categories', permission: PERMISSIONS.MATERIAL_VIEW, Icon: CategoryOutlinedIcon, Component: CategoryMaster },
  // Sits next to Categories — both are the small supporting masters the Material master's own
  // pickers read from, and both are gated by the same Material View/Manage permissions.
  { key: 'uom', labelKey: 'tabs.uom', permission: PERMISSIONS.MATERIAL_VIEW, Icon: StraightenOutlinedIcon, Component: UomMaster },
  {
    // Two independent consumers, either of which is reason enough to expose the master:
    //   Production — the module's common master AND Production Plan specifically on (BOM has
    //     nothing to drive without production planning), hidden again once 3PL is on since a pure
    //     3PL warehouse doesn't manufacture.
    //   Job Work — the module on AND "BOM-Based Sending" on, because that mode explodes the output
    //     material's Active BOM to decide what gets sent to the vendor. Deliberately NOT hidden
    //     under 3PL, matching the Job Work nav's own gating: a 3PL warehouse can legitimately send
    //     a client's goods out for processing. Without this arm, a company running Job Work in BOM
    //     mode with no Production module had no way to create the very BOMs it explodes.
    //   Weigh & Label — its BOM-based plan mode explodes the output item's Active BOM the same way,
    //     so a company running only this module still needs somewhere to author those BOMs.
    key: 'bom', labelKey: 'tabs.bom', permission: PERMISSIONS.BOM_VIEW,
    configPredicate: (c) =>
      (c.productionModuleEnabled && c.productionEnabled && !c.thirdPartyLogisticsEnabled) ||
      (c.jobWorkEnabled && c.jobWorkBomEnabled) ||
      c.weightProductionEnabled,
    Icon: AccountTreeOutlinedIcon, Component: BomMaster,
  },
  { key: 'suppliers', labelKey: 'tabs.suppliers', permission: PERMISSIONS.SUPPLIER_VIEW, Icon: LocalShippingOutlinedIcon, Component: SupplierMaster },
  { key: 'customers', labelKey: 'tabs.customers', permission: PERMISSIONS.CUSTOMER_VIEW, Icon: GroupsOutlinedIcon, Component: CustomerMaster },
  { key: 'carriers', labelKey: 'tabs.carriers', permission: PERMISSIONS.MATERIAL_VIEW, Icon: AirportShuttleOutlinedIcon, Component: CarrierMaster },
  {
    key: 'clients', labelKey: 'tabs.clients', permission: PERMISSIONS.CLIENT_VIEW, configKey: 'thirdPartyLogisticsEnabled',
    Icon: BusinessCenterOutlinedIcon, Component: ClientMaster,
  },
  {
    // BillingEnabled always mirrors ThirdPartyLogisticsEnabled now (see
    // ProcessConfigurationService) — gating on the 3PL flag alone is equivalent and simpler.
    key: 'clientContracts', labelKey: 'tabs.clientContracts', permission: PERMISSIONS.CLIENT_CONTRACT_VIEW,
    configKey: 'thirdPartyLogisticsEnabled',
    Icon: RequestQuoteOutlinedIcon, Component: ClientContractMaster,
  },
  { key: 'qcCheckpoints', labelKey: 'tabs.qcCheckpoints', permission: PERMISSIONS.QC_CHECKPOINT_VIEW, Icon: FactCheckOutlinedIcon, Component: QcCheckpointMaster },
];

export default function MastersPage() {
  const { t } = useTranslation('masters');
  const { hasPermission } = useAuth();
  const { data: processConfiguration } = useProcessConfiguration();

  // Mirrors AppShell.tsx's passesConfigGate exactly — stays visible until config has loaded (no
  // flash-hide on first render), then every one of its flag(s) must be true. A tab supplying a
  // configPredicate opts out of the flat keys entirely and decides for itself (see BOM).
  const passesConfigGate = (tabDef: MasterTabDef) => {
    if (!processConfiguration) return true;
    if (tabDef.configPredicate) return tabDef.configPredicate(processConfiguration);
    if (!tabDef.configKey) return true;
    const keys = Array.isArray(tabDef.configKey) ? tabDef.configKey : [tabDef.configKey];
    return keys.every((key) => processConfiguration[key]);
  };

  // Mirrors AppShell.tsx's passesHiddenWhenGate — hides the tab once any hiddenWhenConfigKey
  // flag is true.
  const passesHiddenWhenGate = (tabDef: MasterTabDef) => {
    if (!tabDef.hiddenWhenConfigKey || !processConfiguration) return true;
    const keys = Array.isArray(tabDef.hiddenWhenConfigKey) ? tabDef.hiddenWhenConfigKey : [tabDef.hiddenWhenConfigKey];
    return !keys.some((key) => processConfiguration[key]);
  };

  const visibleTabs = useMemo(
    () => MASTER_TABS.filter((tabDef) => hasPermission(tabDef.permission) && passesConfigGate(tabDef) && passesHiddenWhenGate(tabDef)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasPermission, processConfiguration],
  );
  const [tab, setTab] = useState(0);
  const activeIndex = Math.min(tab, Math.max(visibleTabs.length - 1, 0));

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      {visibleTabs.length === 0 ? (
        <Typography color="text.secondary">{t('noAccess')}</Typography>
      ) : (
        <>
          {/* A segmented-control look (filled pill on the selected tab, no underline) reads more
              like a dedicated "module switcher" than the plain underlined Tabs used for in-page
              report/detail sub-views elsewhere — appropriate here since these aren't views of one
              dataset, they're eight unrelated master screens sharing one route. */}
          {/* width: '100%' (not display: 'inline-flex') — an inline-flex wrapper sizes to its
              content by default, which can let the Tabs component's own overflow/scroll-button
              detection miscompute against the true available viewport width once there are more
              tabs than fit (Carriers, Clients, etc. below just get cut off with no way to reach
              them). A plain block-level Paper pinned to 100% width reliably gives Tabs' own
              variant="scrollable"/scrollButtons="auto" the width it needs to detect overflow and
              render its scroll arrows. */}
          <Paper variant="outlined" sx={{ width: '100%', p: 0.75, mb: 3 }}>
            <Tabs
              value={activeIndex}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              slotProps={{ indicator: { sx: { display: 'none' } } }}
              sx={{ minHeight: 40, '& .MuiTabs-flexContainer': { gap: 0.5 } }}
            >
              {visibleTabs.map((tabDef) => (
                <Tab
                  key={tabDef.key}
                  icon={<tabDef.Icon fontSize="small" />}
                  iconPosition="start"
                  label={t(tabDef.labelKey)}
                  sx={{
                    minHeight: 40,
                    borderRadius: radius.md,
                    px: 2,
                    color: 'text.secondary',
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { backgroundColor: 'primary.main' },
                    },
                  }}
                />
              ))}
            </Tabs>
          </Paper>

          {/* Only the active tab is mounted — each master screen runs its own queries/mutations,
              so mounting all five at once would fire every one of their network requests eagerly. */}
          {visibleTabs.map((tabDef, index) =>
            index === activeIndex ? (
              <Box key={tabDef.key}>
                <tabDef.Component />
              </Box>
            ) : null,
          )}
        </>
      )}
    </>
  );
}
